import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { map, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject, Subscription, TeardownLogic } from 'rxjs';

import { addSeconds, differenceInSeconds, isValid } from 'date-fns';
import { SessionStorage } from 'ngx-webstorage';
import { Idle, DEFAULT_INTERRUPTSOURCES } from 'src/libs/ng-idle';

import { ApplicantService } from 'services/applicant/applicant.service';
import { ConfigService } from '../config/config.service';
import { UserService } from '../user/user.service';
import { VERSION_NUMBER, BUILD_TIME } from '../version/version.service';

import {
    AuthTokenRefreshResult,
    LoginResult,
    RecentUseInfo,
    RevalidationInfo,
    BaseRoleInfo,
    TestConnectionInfo,
} from 'services/auth/auth.service.class';
import { UnsavedChangesService } from 'routes/routes.guard';
import { UserInfo } from 'services/user/user.service.class';
import { SystemRole } from 'modules/shared/roles';

import {
    ConfirmationDialogOptions,
    ConfirmationDialogService,
    CountdownDialogService,
    MessageBoxDialogService,
    SplashService,
} from '@polk_county_public_schools/ngx-pcps-dialogs';
import { RoutePaths } from 'routes/route.roles';
import { Nullable } from 'classes/nullable';

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    // 20 minutes (time period to wait for no actions before declaring the user is idle)
    static PRD_IDLECHECKTIME: number = 20 * 60;
    // 2 hours (time period to wait for no actions before declaring the user is idle if we are in a DEV localhost environment)
    static DEV_IDLECHECKTIME: number = 6 * 20 * 60;

    // 1 minute (time period to wait before logging the user out automatically).
    static LOGOUTWARNINGTIME = 60;

    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    @SessionStorage(undefined, null) nextAuthRefresh!: string | null;
    @SessionStorage(undefined, true) private systemOnline = true;

    AvailableRoles: BaseRoleInfo[] = [];
    IdleMonitorTime: number = AuthService.PRD_IDLECHECKTIME;
    HasVersionUpdate: Subject<boolean> = new Subject<boolean>();
    SystemOnlineSubject: BehaviorSubject<TestConnectionInfo | null> = new BehaviorSubject<TestConnectionInfo | null>(null);
    User: UserInfo | null = null;
    VersionNumber: Subject<string> = new Subject<string>();
    IsRefreshing: boolean;

    constructor(
        private appSvc: ApplicantService,
        private http: HttpClient,
        private configService: ConfigService,
        private confdialog: ConfirmationDialogService,
        private countdownDlgSvc: CountdownDialogService,
        private idle: Idle,
        private msgBoxSvc: MessageBoxDialogService,
        private unsavedChangesSvc: UnsavedChangesService,
        private userService: UserService,
        private router: Router,
        private splashService: SplashService
    ) {
        this.IsRefreshing = false;

        // If this is a localhost connection, override the idle monitor time to the longer amount for debugging.
        if (window.location.hostname.toLowerCase().includes('localhost')) this.IdleMonitorTime = AuthService.DEV_IDLECHECKTIME;

        const btime = new Date(BUILD_TIME);

        this.VersionNumber.next(VERSION_NUMBER);

        this.SystemOnlineSubject.next({
            SystemMessage: '',
            Status: this.systemOnline,
            JobsVersion: VERSION_NUMBER,
        });

        // Setup the idle process for when we need to use it later.
        this.idle.setIdle(this.IdleMonitorTime);
        this.idle.setTimeout(1); // We just use the idle monitor to check for full idle. Our countdown timer will take it from there.
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        // This will be the trigger callback when the timeout expires.
        this.subrelease = this.idle.onTimeout.subscribe(() => {
            // Before we do anything, stop the idler from running while we decide what will happen.
            this.idle.stop();
            this.IsRefreshing = false;

            // Pop the countdown timer to see if the user wishes to remain active in the system.
            this.countdownDlgSvc
                .Open({
                    TitleText: 'Session Inactivity Logout',
                    MessageText: `You are about to be automatically logged out of the system due to inactivity. If you wish to stay logged in to the site, please click the "Stay Logged In" button below.`,
                    OKText: 'Stay Logged In',
                    CancelText: 'Log Out',
                    CountdownTime: AuthService.LOGOUTWARNINGTIME,
                })
                .subscribe((ctresult) => {
                    if (!!ctresult) {
                        // User wants to stay logged in - Restart the idler monitor, which will give the user another set of time.
                        this.idle.watch();
                        this.IsRefreshing = true;
                    } else {
                        // User wishes to logout, or the timer expired. Either way, log them out.
                        this.Logout(false, 1);
                    }
                });
        });

        // Set up the subscription to watch for the user being updated, so we can tell if we need to boot up auth token refreshing.
        this.subrelease = this.userService.UserSubject.subscribe((user) => {
            this.User = user;

            // See if we have a User object already known to us. If so, this was a refresh of the page most likely,
            // and thus we need to kick start authtoken refreshing again.
            if (this.User?.AuthToken && !this.IsRefreshing) {
                this.StartIdleMonitoring();
            }
        });
    }

    ngOnDestroy(): void {
        this.CancelIdleMonitoring();
        this._subrelease.unsubscribe();
    }

    // #region Cancel Idle Monitoring

    CancelIdleMonitoring(): void {
        // Cancel the idler (if it is running)
        if (this.idle.isRunning()) {
            this.idle.stop();
        }

        this.IsRefreshing = false;
    }

    // #endregion

    // #region Change Impersonation Target Service Call

    ChangeImpersonationTarget(AuthToken: string, SAPID: string): Observable<LoginResult> {
        return this.http.get<LoginResult>(
            `${this.configService.hostAddress}` +
                `Api/Services/Login/ChangeImpersonationTarget?AuthToken=${AuthToken || ''}&SAPID=${SAPID || ''}`
        );
    }

    // #endregion

    // #region Check Recent Use Service Method - This method simply calls the database to see if the user has done anything

    CheckRecentUse(): Observable<RecentUseInfo> {
        return this.http.get<RecentUseInfo>(this.configService.hostAddress + `api/Login/recentuse`).pipe(
            map((data) => {
                data.LastActionTime = data.LastActionTime ? new Date(data.LastActionTime) : null;

                return data;
            })
        );
    }

    // #endregion

    // #region Get Roles Enum

    GetRolesEnum(rolename: string): SystemRole {
        // tslint:disable-next-line: no-any triple-equals
        const keys = Object.keys(SystemRole).filter((k) => (SystemRole as any)[k] == rolename);

        // tslint:disable-next-line: no-any
        return keys.length > 0 ? (SystemRole as any)[keys[0]] : null;
    }

    // #endregion

    // #region Handle Service Error Service Call

    // tslint:disable-next-line: no-any
    HandleServiceError(error: any): void {
        let status: number | null = null;
        let msg = '';

        if (error instanceof HttpErrorResponse) {
            status = error.status;

            const body = error && error.error;
            msg = body.ExceptionMessage || body.Message || body || 'An unknown error has occurred.';
        } else {
            let errorPacket = error.message || error;
            if (errorPacket != null && errorPacket !== '') {
                try {
                    errorPacket = JSON.parse(errorPacket);
                } catch {
                    errorPacket = errorPacket;
                }
            }
            msg =
                errorPacket != null && errorPacket instanceof Object
                    ? errorPacket.ExceptionMessage || errorPacket.Message || error
                    : errorPacket || 'An unknown error has occurred.';
        }

        this.splashService.CloseSplash();

        // Something else, which should be snackbar displayed.
        this.splashService.SnackbarError(msg, 8000);
    }

    // #endregion

    // #region Logout From System Service Call

    LogoutFromSystem(): Observable<boolean> {
        return this.http.put<boolean>(this.configService.hostAddress + `api/Login`, {});
    }

    // #endregion

    // #region Logout

    Logout(confirmation?: boolean, reason?: number): void {
        const self = this;
        let confObs: Observable<boolean>;

        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            const opts: ConfirmationDialogOptions = {
                TitleText: 'Confirmation Requested',
                MessageText: 'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };

            // confObs = self.OpenConfirmationDialog(opts);
            confObs = self.confdialog.Open(opts);
        } else {
            // No dialog needed.
            confObs = of<boolean>(true).pipe(take(1));
        }

        confObs.subscribe((result) => {
            if (result === true) {
                // User is opting to log out. Proceed.
                self.LogoutFromSystem().subscribe();

                // Stop idle monitoring
                self.CancelIdleMonitoring();

                // Null out the current user.
                self.userService.ClearUser();

                // Snackbar the logout if this was user initiated (reason null or 0)
                if (reason !== void 0 && (reason == null || reason === 0)) {
                    self.splashService.SnackbarSuccess('Successfully logged out.', 5000);
                }

                // Close out all remaining open dialogs
                // self.matDialog.closeAll();
                self.confdialog.CloseAllDialogs();

                // Clear out change tracking.
                self.unsavedChangesSvc.ClearTracking();

                // Navigate to the landing state.
                self.router.navigate([`/${RoutePaths.Landing}`]);
            }
        });
    }

    // #endregion

    // #region Process Login

    ProcessLogin(loginPacket: LoginResult): void {
        const self = this;

        // Roles for a user are returned by the system in priority order. All we must do is retrieve the first role in the UserInfo object and use
        // that as the default role access for this login.
        // const currole = userinfo.Roles?.[0] || null;

        // if (currole) {
        //     // Use this role.
        //     self.userService.SetUserCurrentRole(currole);

        //     return;
        // }

        if (!loginPacket || !loginPacket.AuthToken) {
            self.splashService.SnackbarError('Invalid Login.');
            return;
        }

        switch (loginPacket.AuthToken) {
            case 'emailExists':
                self.msgBoxSvc
                    .Open({
                        TitleText: 'Unable to Login',
                        MessageText:
                            'Invalid account detected. Please contact the Help Desk and reference the ARS system with Error Code 115. Thank you.',
                    })
                    .subscribe();
                break;
            case '110':
            case '120':
                self.msgBoxSvc
                    .Open({
                        TitleText: 'Unable to Login',
                        MessageText: `We are unable to authenticate your account. Please contact the Help Desk and reference the ARS system with Error Code ${loginPacket.AuthToken}.`,
                    })
                    .subscribe();
                break;
            case '-1':
                self.msgBoxSvc
                    .Open({
                        TitleText: 'Account Not Activated',
                        MessageText: `Your account has not been yet been activated. You should receive a confirmation email upon your account creation with instructions on how to activate your account in order to be able to log in. Please check your inbox and/or junk mail folders for this email and follow the instructions to activate your account.`,
                    })
                    .subscribe();
                break;
            default:
                // We have a successful login. Do we have either an actual application, or a userid and email? If not, we need to bail out.
                if (loginPacket.UserID == null && !loginPacket.Application) {
                    self.splashService.SnackbarError('No account information found.');
                    return;
                }

                // We have what we need to proceed. First, let's set the user info into session storage for this session.
                self.userService.SetUser(loginPacket);

                // Next, set the applicant info into sessions storage as well.
                self.appSvc.UpdateApplication(loginPacket.Application);

                //  We can reroute to the home route now.
                self.router.navigate([`/${RoutePaths.Home}`]);
                break;
        }
    }

    // #endregion

    // #region Refresh Authentication Token Service Method

    RefreshAuthToken(): Observable<AuthTokenRefreshResult> {
        return this.http.put<AuthTokenRefreshResult>(this.configService.hostAddress + `api/Login/refresh`, {});
    }

    // #endregion

    // #region Reset User Password Service Call

    ResetUserPassword(password: string, valtoken: string): Observable<string> {
        return this.http.get<string>(
            this.configService.hostAddress +
                `Api/Services/Authentication/ResetUserPassword?Password=${password || ''}&ValToken=${valtoken || ''}`
        );
    }

    // #endregion

    // #region Revalidate Session Service Call

    RevalidateSession(
        Username: string | null | undefined,
        Password: string | null | undefined,
        ImpersonatedUsername: string | null | undefined
    ): Observable<RevalidationInfo> {
        return this.http
            .put<UserInfo>(this.configService.hostAddress + `api/Login/revalidate`, {
                Username: Username || '',
                Password: Password || '',
                ImpersonatedUsername: ImpersonatedUsername || '',
            })
            .pipe(
                map((data) => {
                    return {
                        Error: null,
                        UserInfo: data,
                    } as RevalidationInfo;
                })
            );
    }

    // #endregion

    // #region Set System Online Status

    SetSystemOnlineStatus(status: TestConnectionInfo): void {
        this.systemOnline = status?.Status || false;
        if (status)
            status.SystemMessage = !this.systemOnline
                ? status.SystemMessage ||
                  `We're sorry, but the Applicant Registration System is currently unavailable. We apologize for the
                        inconvenience. This page will continue checking to see if the system has become available again, and will
                        transfer you to the login page when the system is back online. Thank you for your patience during this
                        outage.`
                : '';
        this.SystemOnlineSubject.next(status);
    }

    // #endregion

    // #region Staff Portal SSO Login

    StaffPortalSSOLogin(authToken: Nullable<string>, sysToken: Nullable<string>, screen: Nullable<string>, win: Nullable<string>) {
        return this.http
            .post<LoginResult | null>(this.configService.hostAddress + 'api/Login/staffSSO', {
                AuthToken: authToken || '',
                SysToken: sysToken || '',
                Screen: screen || '',
                Window: win || '',
            })
            .pipe(
                map((data) => {
                    if (data?.Application) {
                        data.Application.DateOfBirth = data.Application.DateOfBirth ? new Date(data.Application.DateOfBirth) : null;
                        data.Application.DateSubmitted = data.Application.DateSubmitted
                            ? new Date(data.Application.DateSubmitted)
                            : null;
                        data.Application.DateUpdated = data.Application.DateUpdated ? new Date(data.Application.DateUpdated) : null;
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Start Idle Monitoring

    StartIdleMonitoring(): void {
        // This process will work as follows: We initiate a previously configured idler monitor to keep an eye on user activity in the
        // application. If the application reaches a set amount of time with no user interaction, we consider the application to be
        // timed out, and we will launch a 1 minute countdown timer, after which time an auto-logout will take place.

        // If the idler is currently running, we should stop it first.
        if (this.idle.isRunning()) {
            this.idle.stop();
        }

        // Relaunch the idler monitoring.
        this.idle.watch();

        this.IsRefreshing = true;
    }

    // #endregion

    // #region Test Connection Service Call

    TestConnection(): Observable<TestConnectionInfo> {
        return this.http.get<TestConnectionInfo>(this.configService.hostAddress + 'api/Login/testconnection');
    }

    // #endregion

    // #region Validate User

    ValidateUser(
        username: string,
        password: string,
        impersonatedUser?: string,
        training?: boolean,
        screen?: string,
        window?: string
    ): Observable<LoginResult> {
        return this.http
            .post<LoginResult>(this.configService.hostAddress + `api/Login`, {
                Email: username || '',
                Password: password || '',
                TrainingMode: training || false,
                ImpersonatedEmail: impersonatedUser || '',
                Screen: screen || '',
                Window: window || '',
            })
            .pipe(
                map((data) => {
                    if (data?.Application) {
                        data.Application.DateOfBirth = data.Application.DateOfBirth ? new Date(data.Application.DateOfBirth) : null;
                        data.Application.DateSubmitted = data.Application.DateSubmitted
                            ? new Date(data.Application.DateSubmitted)
                            : null;
                        data.Application.DateUpdated = data.Application.DateUpdated ? new Date(data.Application.DateUpdated) : null;
                    }

                    return data;
                })
            );
    }

    // #endregion
}
